import { PushNotifications } from "@capacitor/push-notifications";
import { LocalNotifications } from "@capacitor/local-notifications";
import { Capacitor } from "@capacitor/core";
import { Preferences } from '@capacitor/preferences';

// Every token value this device has reported during this app session.
const seenTokens = new Set<string>();

/**
 * All push tokens that may belong to this device: the one in React state, any
 * reported this session, and the FCM token the iOS AppDelegate stores.
 */
export async function getDevicePushTokens(current?: string | null): Promise<string[]> {
  const tokens = new Set(seenTokens);
  if (current) tokens.add(current);
  if (Capacitor.getPlatform() !== "web") {
    try {
      const { value } = await Preferences.get({ key: "push_token_debug" });
      if (value) tokens.add(value);
    } catch {
      // ignore
    }
  }
  return [...tokens];
}

export const setupPush = async ({
  onToken,
  onNotification,
  onActionPerformed,
}: {
  onToken?: (token: string) => void;
  onNotification?: (notification: any) => void;
  onActionPerformed?: (notification: any) => void;
}) => {
  // Only run on native platforms, not web
  if (Capacitor.getPlatform() === "web") {
    console.log("Push notifications are not available on web platform");
    return;
  }

  try {
    // 1. Check and request push permissions FIRST
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      const newStatus = await PushNotifications.requestPermissions();
      if (newStatus.receive !== 'granted') {
        console.warn("Push notification permission prompt denied");
        return;
      }
    } else if (permStatus.receive !== 'granted') {
      // If denied, we can't do anything
      console.warn("Push notification permission previously denied");
      return;
    }

    // 2. Add listeners only after we know we have permission intent
    await PushNotifications.removeAllListeners();
    console.log("📱 [Setup] Removed all previous listeners");

    await PushNotifications.addListener("registration", (token) => {
      console.log("✅ [Listener] Push registration success, token: " + token.value);
      seenTokens.add(token.value);
      if (onToken) onToken(token.value);
    });
    console.log("📱 [Setup] Added 'registration' listener");

    await PushNotifications.addListener("registrationError", (error: any) => {
      console.error("❌ [Listener] Push registration error: " + JSON.stringify(error));
    });
    console.log("📱 [Setup] Added 'registrationError' listener");

    await PushNotifications.addListener(
      "pushNotificationReceived",
      async (notification) => {
        console.log("🔔 [PUSH RECEIVED] Notification arrived:", notification);
        console.log("🔔 [PUSH RECEIVED] Data:", JSON.stringify(notification.data));
        console.log("🔔 [PUSH RECEIVED] Notification object:", JSON.stringify(notification.notification));

        // Get title and body from data payload (preferred) or notification payload
        const title = notification.data?.title || notification.notification?.title || "New Message";
        const body = notification.data?.body || notification.notification?.body || "";
        const notId = Math.floor(Date.now() / 1000); // Unique int ID



        if (onNotification) onNotification(notification.notification);
      }
    );
    console.log("📱 [Setup] Added 'pushNotificationReceived' listener");

    await PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (notification) => {
        console.log("👆 [Action] Push action performed: ", notification);
        if (onActionPerformed) onActionPerformed(notification.notification);
      }
    );
    console.log("📱 [Setup] Added 'pushNotificationActionPerformed' listener");

    // 3. Register for push notifications
    console.log("📱 [Setup] Registering for push notifications...");
    await PushNotifications.register();

    // 4. Request local notifications permission explicitly
    try {
      await LocalNotifications.requestPermissions();
    } catch (error) {
      console.warn("Local notifications permission request failed:", error);
    }

    // 5. Fallback: Check for stored token if event listener doesn't fire
    setTimeout(async () => {
      try {
        const { value } = await Preferences.get({ key: 'push_token_debug' });
        if (value) {
          console.log("✅ [Fallback] Found stored push token: " + value);
          seenTokens.add(value);
          if (onToken) onToken(value);
        } else {
          console.log("⚠️ [Fallback] No stored push token found.");
        }
      } catch (e) {
        console.error("❌ [Fallback] Error reading token:", e);
      }
    }, 5000);

  } catch (error) {
    // Silently handle errors - push notifications are optional
    console.log(
      "Push notifications setup failed (this is expected on web):",
      error
    );
  }
};
